import { c as _c } from "react/compiler-runtime";
import type { InitialTableState } from '@tanstack/react-table';
import usePagination from '@mui/material/usePagination';
import { createColumnHelper, getCoreRowModel, getExpandedRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import clsx from 'clsx';
import { identity } from 'es-toolkit';
import { useSessionStorage } from 'foxact/use-session-storage';
import { atom, useAtom, useAtomValue, useStore } from 'jotai';
import { useImmerAtom } from 'jotai-immer';
import { startTransition, useDeferredValue, useRef } from 'react';
import type { LearningPhase } from '@/app/[locale]/(vocabulary)/_lib/LexiconTrie';
import type { VocabularySourceData, VocabularySourceState } from '@/app/[locale]/(vocabulary)/_lib/vocab';
import type { ColumnFilterFn } from '@/lib/table-utils';
import { STATUS_LABELS, userVocabularyAtom } from '@/app/[locale]/(vocabulary)/_api';
import { vocabSubscriptionAtom } from '@/app/[locale]/(vocabulary)/_atoms';
import { AcquaintAllDialog } from '@/app/[locale]/(vocabulary)/_components/acquaint-all-dialog';
import { useVocabularyCommonColumns } from '@/app/[locale]/(vocabulary)/_components/columns';
import { VocabularyMenu } from '@/app/[locale]/(vocabulary)/_components/menu';
import { VocabStatics } from '@/app/[locale]/(vocabulary)/_components/statics-bar';
import { searchFilterValue } from '@/app/[locale]/(vocabulary)/_lib/filters';
import { LEARNING_PHASE } from '@/app/[locale]/(vocabulary)/_lib/LexiconTrie';
import { useManagedVocabulary } from '@/app/[locale]/(vocabulary)/_lib/vocab-utils';
import { TableGoToLastPage } from '@/components/my-table/go-to-last-page';
import { TablePagination } from '@/components/my-table/pagination';
import { TablePaginationSizeSelect } from '@/components/my-table/pagination-size-select';
import { SearchWidget } from '@/components/search-widget';
import { DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Div } from '@/components/ui/html-elements';
import { SegmentedControl, SegmentItem } from '@/components/ui/segmented-control';
import { Spinner } from '@/components/ui/spinner';
import { HeaderTitle, TableDataCell, TableHeader, TableHeaderCell, TableHeaderCellRender, TableRow } from '@/components/ui/table-element';
import { combineFilters, noFilter } from '@/lib/table-utils';
import { cn } from '@/lib/utils';
import { useI18n } from '@/locales/client';
import { useClone, useLastTruthy } from '@sub-vocab/utils/hooks';
import { customFormatDistance, customFormatDistanceToNowStrict, findClosest, formatDistanceLocale, getFallBack, isRegexValid } from '@sub-vocab/utils/lib';
import { narrow } from '@sub-vocab/utils/types';
type TableData = VocabularySourceState;

/// keep-unique
const PAGE_SIZES = [10, 20, 40, 50, 100, 200, 500, 1000] as const;
const cacheStateAtom = atom({
  isUsingRegex: false,
  searchValue: ''
});
const initialTableStateAtom = atom(identity<InitialTableState>({
  columnOrder: ['timeModified', 'word', 'word.length', 'acquaintedStatus', 'rank'],
  pagination: {
    pageSize: findClosest(100, PAGE_SIZES),
    pageIndex: 0
  },
  sorting: [{
    id: 'timeModified',
    desc: true
  }],
  columnVisibility: {}
}));
function useSegments() {
  const $ = _c(2);
  const t = useI18n();
  let t0;
  if ($[0] !== t) {
    t0 = narrow([{
      value: "new",
      label: t("recent")
    }, {
      value: "allAcquainted",
      label: t("all")
    }, {
      value: "mine",
      label: t("mine")
    }, {
      value: "top",
      label: t("top")
    }]);
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
type Segment = ReturnType<typeof useSegments>[number]['value'];
const SEGMENT_NAME = 'data-table-segment';
function acquaintedStatusFilter(filterSegment: Segment): ColumnFilterFn<TableData> {
  let filteredValue: LearningPhase[] = [];
  if (filterSegment === 'new') {
    filteredValue = [LEARNING_PHASE.NEW, LEARNING_PHASE.RETAINING];
  } else if (filterSegment === 'allAcquainted' || filterSegment === 'mine' || filterSegment === 'top') {
    filteredValue = [LEARNING_PHASE.ACQUAINTED, LEARNING_PHASE.FADING];
  } else {
    return noFilter;
  }
  return row => filteredValue.includes(row.inertialPhase);
}
function userOwnedFilter(filterSegment: Segment): ColumnFilterFn<TableData> {
  let filteredValue: boolean[] = [];
  if (filterSegment === 'top') {
    filteredValue = [false];
  } else if (filterSegment === 'mine') {
    filteredValue = [true];
  } else {
    return noFilter;
  }
  return row => filteredValue.includes(row.trackedWord.isUser);
}
function useDataColumns(rootRef) {
  const $ = _c(5);
  const t = useI18n();
  let t0;
  if ($[0] !== rootRef?.current || $[1] !== t) {
    const columnHelper = createColumnHelper();
    t0 = columnHelper.accessor(_temp, {
      id: "timeModified",
      header: t1 => {
        const {
          header
        } = t1;
        const isSorted = header.column.getIsSorted();
        const title = t("distance");
        return <TableHeaderCell header={header} className="w-[12%] active:bg-background-active"><Div className="pr-1 pl-2 select-none" onClick={e => {
            header.column.getToggleSortingHandler()?.(e);
            requestAnimationFrame(() => rootRef?.current?.scrollTo({
              top: 0
            }));
          }}><HeaderTitle title={title} isSorted={isSorted} className="*:data-title:text-right" /></Div></TableHeaderCell>;
      },
      cell: _temp2
    });
    $[0] = rootRef?.current;
    $[1] = t;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== t0) {
    t1 = [t0];
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
function _temp2(t0) {
  const {
    cell,
    getValue
  } = t0;
  const value = getValue();
  return <TableDataCell cell={cell}><Div className="justify-end pr-2 pl-0.5 tracking-[.03em] tabular-nums">{value ? customFormatDistanceToNowStrict(new Date(value), {
        addSuffix: true,
        locale: {
          formatDistance: customFormatDistance(formatDistanceLocale)
        }
      }) : null}</Div></TableDataCell>;
}
function _temp(row) {
  return row.trackedWord.timeModified;
}
export function VocabDataTable(t0) {
  const $ = _c(119);
  const {
    data: rows,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const [data, handlePurge] = useManagedVocabulary(rows);
  const t = useI18n();
  const [t2, setCacheState] = useImmerAtom(cacheStateAtom);
  const {
    isUsingRegex,
    searchValue
  } = t2;
  const deferredSearchValue = useDeferredValue(searchValue);
  const deferredIsUsingRegex = useDeferredValue(isUsingRegex);
  const rootRef = useRef(null);
  const vocabularyCommonColumns = useVocabularyCommonColumns(rootRef);
  const dataColumns = useDataColumns(rootRef);
  const columns = [...vocabularyCommonColumns, ...dataColumns];
  const segments = useSegments();
  const [segment, setSegment] = useSessionStorage(`${SEGMENT_NAME}-value`, "allAcquainted");
  const segmentDeferredValue = useDeferredValue(segment);
  const lastTruthySearchFilterValue = useLastTruthy(searchFilterValue(deferredSearchValue, deferredIsUsingRegex))() ?? noFilter;
  const inValidSearch = deferredIsUsingRegex && !isRegexValid(deferredSearchValue);
  const {
    refetch,
    isFetching: isLoadingUserVocab
  } = useAtomValue(userVocabularyAtom);
  const store = useStore();
  const table = useClone(useReactTable({
    data,
    columns,
    state: {
      columnFilters: [{
        id: "word",
        value: combineFilters([acquaintedStatusFilter(segmentDeferredValue), userOwnedFilter(segmentDeferredValue), lastTruthySearchFilterValue])
      }]
    },
    initialState: store.get(initialTableStateAtom),
    autoResetPageIndex: false,
    getRowId: _temp3,
    getRowCanExpand: _temp4,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onStateChange: updater => {
      store.set(initialTableStateAtom, typeof updater === "function" ? updater(tableState) : updater);
    },
    getSortedRowModel: getSortedRowModel()
  }));
  const tableState = table.getState();
  let t3;
  if ($[0] !== table) {
    t3 = table.getPageCount();
    $[0] = table;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = tableState.pagination.pageIndex + 1;
  let t5;
  if ($[2] !== t3 || $[3] !== t4) {
    t5 = {
      count: t3,
      page: t4
    };
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const {
    items
  } = usePagination(t5);
  let rowsAcquainted;
  let rowsFiltered;
  let rowsNew;
  let t6;
  if ($[5] !== table) {
    rowsFiltered = table.getFilteredRowModel().rows;
    const {
      rowsAcquainted: t7,
      rowsNew: t8
    } = Object.groupBy(rowsFiltered, _temp5);
    rowsAcquainted = t7 === undefined ? [] : t7;
    rowsNew = t8 === undefined ? [] : t8;
    t6 = rowsNew.map(_temp6);
    $[5] = table;
    $[6] = rowsAcquainted;
    $[7] = rowsFiltered;
    $[8] = rowsNew;
    $[9] = t6;
  } else {
    rowsAcquainted = $[6];
    rowsFiltered = $[7];
    rowsNew = $[8];
    t6 = $[9];
  }
  const rowsToRetain = t6;
  const isStale = segment !== segmentDeferredValue || searchValue !== deferredSearchValue || isUsingRegex !== deferredIsUsingRegex;
  const [vocabRealtimeSubscribeState] = useAtom(vocabSubscriptionAtom);
  let t7;
  if ($[10] !== className) {
    t7 = cn("flex h-full flex-col items-center overflow-hidden bg-background will-change-transform", className);
    $[10] = className;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t) {
    t8 = t("Options");
    $[12] = t;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = <DropdownMenuLabel>{t8}</DropdownMenuLabel>;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <DropdownMenuSeparator />;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== refetch) {
    t11 = e => {
      refetch();
    };
    $[17] = refetch;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const t12 = isLoadingUserVocab ? "" : "hidden";
  let t13;
  if ($[19] !== t12) {
    t13 = clsx("icon-[lucide--loader-2] size-3.5 animate-spin", t12);
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t13) {
    t14 = <svg className={t13} />;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const t15 = isLoadingUserVocab ? "hidden" : "";
  let t16;
  if ($[23] !== t15) {
    t16 = clsx("icon-[ion--refresh] size-3.5", t15);
    $[23] = t15;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== t16) {
    t17 = <svg className={t16} />;
    $[25] = t16;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div>Refresh</div>;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== isLoadingUserVocab || $[29] !== t11 || $[30] !== t14 || $[31] !== t17) {
    t19 = <DropdownMenuItem disabled={isLoadingUserVocab} className="gap-3" onClick={t11}>{t14}{t17}{t18}</DropdownMenuItem>;
    $[28] = isLoadingUserVocab;
    $[29] = t11;
    $[30] = t14;
    $[31] = t17;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  let t21;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = clsx("gap-3");
    t21 = <div className="size-3.5" />;
    $[33] = t20;
    $[34] = t21;
  } else {
    t20 = $[33];
    t21 = $[34];
  }
  const t22 = vocabRealtimeSubscribeState ?? "";
  let t23;
  if ($[35] !== t22) {
    t23 = getFallBack(t22, STATUS_LABELS);
    $[35] = t22;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  let t24;
  if ($[37] !== t23) {
    t24 = <DropdownMenuItem disabled={true} className={t20}>{t21}<div>{t23}</div></DropdownMenuItem>;
    $[37] = t23;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] !== rowsToRetain) {
    t25 = <DropdownMenuItem onSelect={_temp7} className="justify-between p-0"><AcquaintAllDialog vocabulary={rowsToRetain} /></DropdownMenuItem>;
    $[39] = rowsToRetain;
    $[40] = t25;
  } else {
    t25 = $[40];
  }
  let t26;
  if ($[41] !== t19 || $[42] !== t24 || $[43] !== t25) {
    t26 = <DropdownMenuGroup>{t19}{t24}{t25}</DropdownMenuGroup>;
    $[41] = t19;
    $[42] = t24;
    $[43] = t25;
    $[44] = t26;
  } else {
    t26 = $[44];
  }
  let t27;
  if ($[45] !== t26 || $[46] !== t9) {
    t27 = <VocabularyMenu>{t9}{t10}{t26}</VocabularyMenu>;
    $[45] = t26;
    $[46] = t9;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  const t28 = isStale ? "" : "opacity-0";
  let t29;
  if ($[48] !== t28) {
    t29 = clsx("flex justify-center transition-opacity delay-300 duration-200", t28);
    $[48] = t28;
    $[49] = t29;
  } else {
    t29 = $[49];
  }
  let t30;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <Spinner variant="primary" size="sm" />;
    $[50] = t30;
  } else {
    t30 = $[50];
  }
  let t31;
  if ($[51] !== t29) {
    t31 = <div className="flex items-center px-2.5 text-base"><div className={t29}>{t30}</div></div>;
    $[51] = t29;
    $[52] = t31;
  } else {
    t31 = $[52];
  }
  let t32;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <div className="grow" />;
    $[53] = t32;
  } else {
    t32 = $[53];
  }
  let t33;
  let t34;
  if ($[54] !== setCacheState) {
    t33 = v => {
      setCacheState(draft => {
        draft.searchValue = v;
      });
    };
    t34 = v_0 => {
      setCacheState(draft_0 => {
        draft_0.isUsingRegex = v_0;
      });
    };
    $[54] = setCacheState;
    $[55] = t33;
    $[56] = t34;
  } else {
    t33 = $[55];
    t34 = $[56];
  }
  const t35 = inValidSearch ? "!border-red-500" : "";
  let t36;
  if ($[57] !== isUsingRegex || $[58] !== searchValue || $[59] !== t33 || $[60] !== t34 || $[61] !== t35) {
    t36 = <SearchWidget value={searchValue} isUsingRegex={isUsingRegex} onSearch={t33} onRegex={t34} className={t35} />;
    $[57] = isUsingRegex;
    $[58] = searchValue;
    $[59] = t33;
    $[60] = t34;
    $[61] = t35;
    $[62] = t36;
  } else {
    t36 = $[62];
  }
  let t37;
  if ($[63] !== t27 || $[64] !== t31 || $[65] !== t36) {
    t37 = <div className="z-10 flex h-12 w-full justify-between bg-background p-2">{t27}{t31}{t32}{t36}</div>;
    $[63] = t27;
    $[64] = t31;
    $[65] = t36;
    $[66] = t37;
  } else {
    t37 = $[66];
  }
  let t38;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <div className="h-px w-full border-b border-border" />;
    $[67] = t38;
  } else {
    t38 = $[67];
  }
  let t39;
  if ($[68] !== handlePurge || $[69] !== setSegment) {
    t39 = newSegment => {
      setSegment(newSegment);
      startTransition(() => {
        handlePurge();
      });
    };
    $[68] = handlePurge;
    $[69] = setSegment;
    $[70] = t39;
  } else {
    t39 = $[70];
  }
  let t40;
  if ($[71] !== segments) {
    t40 = segments.map(_temp8);
    $[71] = segments;
    $[72] = t40;
  } else {
    t40 = $[72];
  }
  let t41;
  if ($[73] !== segment || $[74] !== t39 || $[75] !== t40) {
    t41 = <div className="z-10 w-full bg-background"><SegmentedControl value={segment} onValueChange={t39} variant="ghost">{t40}</SegmentedControl></div>;
    $[73] = segment;
    $[74] = t39;
    $[75] = t40;
    $[76] = t41;
  } else {
    t41 = $[76];
  }
  let t42;
  if ($[77] !== table) {
    t42 = table.getHeaderGroups().map(_temp10);
    $[77] = table;
    $[78] = t42;
  } else {
    t42 = $[78];
  }
  let t43;
  if ($[79] !== t42) {
    t43 = <TableHeader>{t42}</TableHeader>;
    $[79] = t42;
    $[80] = t43;
  } else {
    t43 = $[80];
  }
  let t44;
  if ($[81] !== table) {
    t44 = table.getRowModel().rows.map(_temp11);
    $[81] = table;
    $[82] = t44;
  } else {
    t44 = $[82];
  }
  let t45;
  if ($[83] !== t44) {
    t45 = <tbody>{t44}</tbody>;
    $[83] = t44;
    $[84] = t45;
  } else {
    t45 = $[84];
  }
  let t46;
  if ($[85] !== t43 || $[86] !== t45) {
    t46 = <table className="relative min-w-full border-separate border-spacing-0">{t43}{t45}</table>;
    $[85] = t43;
    $[86] = t45;
    $[87] = t46;
  } else {
    t46 = $[87];
  }
  let t47;
  if ($[88] !== table) {
    t47 = <TableGoToLastPage table={table} />;
    $[88] = table;
    $[89] = t47;
  } else {
    t47 = $[89];
  }
  let t48;
  if ($[90] !== t46 || $[91] !== t47) {
    t48 = <div ref={rootRef} className="z-1 w-full grow overflow-auto overflow-y-scroll overscroll-contain [scrollbar-width:thin]">{t46}{t47}</div>;
    $[90] = t46;
    $[91] = t47;
    $[92] = t48;
  } else {
    t48 = $[92];
  }
  let t49;
  if ($[93] !== items || $[94] !== table) {
    t49 = <TablePagination items={items} table={table} rootRef={rootRef} />;
    $[93] = items;
    $[94] = table;
    $[95] = t49;
  } else {
    t49 = $[95];
  }
  let t50;
  if ($[96] !== table || $[97] !== tableState.pagination.pageSize) {
    t50 = <TablePaginationSizeSelect table={table} sizes={PAGE_SIZES} value={tableState.pagination.pageSize} />;
    $[96] = table;
    $[97] = tableState.pagination.pageSize;
    $[98] = t50;
  } else {
    t50 = $[98];
  }
  const t51 = `/${t("page")}`;
  let t52;
  if ($[99] !== t51) {
    t52 = <div className="px-1 whitespace-nowrap">{t51}</div>;
    $[99] = t51;
    $[100] = t52;
  } else {
    t52 = $[100];
  }
  let t53;
  if ($[101] !== t50 || $[102] !== t52) {
    t53 = <div className="flex grow items-center justify-end"><div className="flex items-center text-xs">{t50}{t52}</div></div>;
    $[101] = t50;
    $[102] = t52;
    $[103] = t53;
  } else {
    t53 = $[103];
  }
  let t54;
  if ($[104] !== t49 || $[105] !== t53) {
    t54 = <div className="flex w-full flex-wrap items-center justify-between gap-0.5 border-t border-t-zinc-200 py-1 pr-0.5 tracking-[.03em] tabular-nums dark:border-neutral-800">{t49}{t53}</div>;
    $[104] = t49;
    $[105] = t53;
    $[106] = t54;
  } else {
    t54 = $[106];
  }
  const t55 = ` ${t("vocabulary")}`;
  let t56;
  if ($[107] !== rowsAcquainted.length || $[108] !== rowsFiltered.length || $[109] !== rowsNew.length || $[110] !== t55) {
    t56 = <div className="flex w-full justify-center border-t border-solid border-t-zinc-200 bg-background dark:border-neutral-800"><VocabStatics total={rowsFiltered.length} text={t55} remaining={rowsNew.length} completed={rowsAcquainted.length} /></div>;
    $[107] = rowsAcquainted.length;
    $[108] = rowsFiltered.length;
    $[109] = rowsNew.length;
    $[110] = t55;
    $[111] = t56;
  } else {
    t56 = $[111];
  }
  let t57;
  if ($[112] !== t37 || $[113] !== t41 || $[114] !== t48 || $[115] !== t54 || $[116] !== t56 || $[117] !== t7) {
    t57 = <div className={t7}>{t37}{t38}{t41}{t48}{t54}{t56}</div>;
    $[112] = t37;
    $[113] = t41;
    $[114] = t48;
    $[115] = t54;
    $[116] = t56;
    $[117] = t7;
    $[118] = t57;
  } else {
    t57 = $[118];
  }
  return t57;
}
function _temp11(row_2) {
  return <TableRow key={row_2.id} row={row_2} />;
}
function _temp10(headerGroup) {
  return <tr key={headerGroup.id}>{headerGroup.headers.map(_temp9)}</tr>;
}
function _temp9(header) {
  return <TableHeaderCellRender key={header.id} header={header} />;
}
function _temp8(segment_0) {
  return <SegmentItem key={segment_0.value} segment={segment_0} />;
}
function _temp7(e_0) {
  return e_0.preventDefault();
}
function _temp6(row_1) {
  return row_1.original.trackedWord;
}
function _temp5(row_0) {
  switch (row_0.original.trackedWord.learningPhase) {
    case LEARNING_PHASE.ACQUAINTED:
      {
        return "rowsAcquainted";
      }
    case LEARNING_PHASE.NEW:
      {
        return "rowsNew";
      }
    default:
      {
        return "_";
      }
  }
}
function _temp4() {
  return false;
}
function _temp3(row) {
  return row.trackedWord.form;
}